/**
 * Poll Service
 * Handles real-time polls with voting via Socket.IO
 * Stores polls in-memory (no database persistence)
 * 
 * Features:
 * - Create polls with multiple options
 * - Real-time voting
 * - Vote counting and percentages
 * - Auto-close when meeting ends
 * - Manual close option
 */

class PollService {
  constructor() {
    this.polls = new Map(); // { pollId: poll object }
    this.activePoll = null; // Currently displayed poll
    this.socket = null;
    this.listeners = {};
    this.pollIdCounter = 0;
    console.log('🗳️ Poll service initialized');
  }

  /**
   * Set Socket.IO instance
   */
  setSocket(socket) {
    this.socket = socket;
    console.log('🗳️ Socket.IO instance set for polls');
  }

  /**
   * Create a new poll
   */
  createPoll(question, options, createdBy) {
    if (!question || !options || options.length < 2) {
      console.error('🗳️ Invalid poll: need question and 2+ options');
      return false;
    }

    const pollId = `poll-${Date.now()}-${this.pollIdCounter++}`;
    const poll = {
      id: pollId,
      question,
      options: options.map((opt, idx) => ({
        id: `opt-${idx}`,
        text: opt,
        votes: 0,
        voters: [] // Track who voted for this option
      })),
      createdBy,
      createdAt: new Date(),
      totalVotes: 0,
      isClosed: false
    };

    this.polls.set(pollId, poll);
    this.activePoll = pollId;

    console.log(`🗳️ Poll created: ${question}`, { id: pollId, options: options.length });
    console.log('🗳️ Poll object:', poll);
    console.log('🗳️ Socket instance:', this.socket ? 'Connected' : 'Not connected');

    // Emit to server
    if (this.socket && this.socket.connected) {
      console.log('🗳️ Emitting poll-created to server');
      this.socket.emit('create-poll', poll);
    } else {
      console.warn('🗳️ Socket not ready. Socket:', this.socket ? 'exists' : 'null', 'Connected:', this.socket?.connected);
      // Still emit locally for immediate feedback
    }

    console.log('🗳️ Emitting poll-created event locally');
    this.emit('poll-created', poll);
    return poll;
  }

  /**
   * Vote on a poll option
   */
  /** Keep a poll that another participant created, so this browser can vote on it. */
  receivePoll(poll) {
    if (!poll || !poll.id) return;
    this.polls.set(poll.id, poll);
    this.activePoll = poll.id;
  }

  vote(pollId, optionId, voterId) {
    const poll = this.polls.get(pollId);
    if (!poll) {
      console.error('🗳️ Poll not found:', pollId);
      return false;
    }

    if (poll.isClosed) {
      console.warn('🗳️ Poll is closed');
      return false;
    }

    const option = poll.options.find(o => o.id === optionId);
    if (!option) {
      console.error('🗳️ Option not found:', optionId);
      return false;
    }

    // Check if voter already voted
    const hasVoted = poll.options.some(o => o.voters.includes(voterId));
    if (hasVoted) {
      console.warn('🗳️ Voter already voted:', voterId);
      return false;
    }

    // Record vote
    option.votes++;
    option.voters.push(voterId);
    poll.totalVotes++;

    console.log(`🗳️ Vote recorded: ${option.text}`, { 
      pollId, 
      votes: option.votes,
      totalVotes: poll.totalVotes 
    });

    // Emit to server
    if (this.socket && this.socket.connected) {
      this.socket.emit('vote-on-poll', {
        pollId,
        optionId,
        voterId,
        poll: this.getPollWithPercentages(poll)
      });
    }

    this.emit('vote-recorded', {
      pollId,
      optionId,
      poll: this.getPollWithPercentages(poll)
    });

    return true;
  }

  /**
   * Get poll with vote percentages calculated
   */
  getPollWithPercentages(poll) {
    const pollWithPercentages = JSON.parse(JSON.stringify(poll)); // Deep copy
    pollWithPercentages.options = pollWithPercentages.options.map(option => ({
      ...option,
      percentage: poll.totalVotes > 0 
        ? Math.round((option.votes / poll.totalVotes) * 100) 
        : 0
    }));
    return pollWithPercentages;
  }

  /**
   * Close a poll
   */
  closePoll(pollId) {
    const poll = this.polls.get(pollId);
    if (!poll) {
      console.error('🗳️ Poll not found:', pollId);
      return false;
    }

    poll.isClosed = true;
    console.log(`🗳️ Poll closed: ${poll.question}`, { id: pollId });

    // Emit to server
    if (this.socket && this.socket.connected) {
      this.socket.emit('close-poll', { pollId });
    }

    this.emit('poll-closed', poll);
    return true;
  }

  /**
   * Get active poll
   */
  getActivePoll() {
    if (!this.activePoll) return null;
    const poll = this.polls.get(this.activePoll);
    return poll ? this.getPollWithPercentages(poll) : null;
  }

  /**
   * Get all polls
   */
  getAllPolls() {
    return Array.from(this.polls.values()).map(poll => this.getPollWithPercentages(poll));
  }

  /**
   * Clear all polls (on meeting end)
   */
  clearPolls() {
    this.polls.clear();
    this.activePoll = null;
    console.log('🗳️ All polls cleared');
  }

  /**
   * Event listener registration
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
}

// Export singleton instance
const pollService = new PollService();
export default pollService;
